import React, { Fragment, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { X } from 'lucide-react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  appearance?: 'default' | 'product';
  titleIcon?: React.ReactNode;
  titleIconWrapperClassName?: string;
  headerSlot?: React.ReactNode;
  afterLeave?: () => void;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  appearance = 'default',
  titleIcon,
  titleIconWrapperClassName,
  headerSlot,
  afterLeave,
}) => {
  const cancelButtonRef = useRef(null);

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
  };

  const isProduct = appearance === 'product';

  const panelClassName = isProduct
    ? 'rounded-xl border border-gray-100 shadow-xl'
    : 'rounded-lg shadow-xl';

  const innerClassName = isProduct ? 'bg-white px-6 pt-6 pb-6 text-base' : 'bg-white p-6';

  const titleClassName = isProduct ? 'text-lg font-semibold text-gray-900' : 'text-lg font-medium text-gray-900';

  const closeButtonClassName = isProduct
    ? 'rounded-md p-0.5 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary/40'
    : 'rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500';

  const iconBoxClass = titleIconWrapperClassName ?? 'bg-[#fdf5f5]';

  const stopInteractionPropagation = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  return (
    <Transition.Root show={isOpen} as={Fragment} afterLeave={afterLeave}>
      <Dialog
        as="div"
        className={`fixed inset-0 overflow-y-auto ${isProduct ? 'z-[60]' : 'z-50'}`}
        initialFocus={cancelButtonRef}
        onClose={onClose}
      >
        <div
          className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
          onClick={stopInteractionPropagation}
          onMouseDown={stopInteractionPropagation}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={stopInteractionPropagation}
              onMouseDown={stopInteractionPropagation}
            />
          </Transition.Child>

          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
            &#8203;
          </span>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div
              className={`inline-block align-bottom bg-white text-left overflow-hidden transform transition-all sm:my-8 sm:align-middle sm:w-full ${panelClassName} ${sizeClasses[size]}`}
              onClick={stopInteractionPropagation}
              onMouseDown={stopInteractionPropagation}
            >
              <div className={innerClassName}>
                {headerSlot ? (
                  headerSlot
                ) : (
                  <div className="flex items-start justify-between gap-3">
                    {isProduct && titleIcon ? (
                      <div className="flex min-w-0 flex-1 items-center gap-2.5">
                        <div
                          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${iconBoxClass}`}
                        >
                          {titleIcon}
                        </div>
                        <Dialog.Title as="h3" className={`${titleClassName} truncate`}>
                          {title}
                        </Dialog.Title>
                      </div>
                    ) : (
                      <Dialog.Title as="h3" className={titleClassName}>
                        {title}
                      </Dialog.Title>
                    )}
                    <div className={`flex shrink-0 items-center ${isProduct ? '' : 'ml-3 h-7'}`}>
                      <button type="button" className={closeButtonClassName} onClick={onClose}>
                        <span className="sr-only">Close</span>
                        <X className={isProduct ? 'h-5 w-5' : 'h-6 w-6'} aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                )}
                <div className="mt-2">{children}</div>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default Modal;
